import React, { useEffect } from 'react'
import { Typography, Button } from '@weirgroup/weir-dls'
import { CustomAlert } from '../../../../components/custom-alert'
import { useTranslation } from 'react-i18next'
import './findings-step.scss'
import { UploadAssetValidationResponse } from '../../../../types/bucketTypes'
import { useAnalyticsEvent } from '../../../../hooks/useAnalyticsEvent'

interface ValidationError {
    row?: number;
    field: string;
    sampleValue: string;
    errorMessage: string;
    suggestedFix: string;
    type?: 'error' | 'warning';
}

interface FindingsStepProps {
    darkMode: boolean;
    fileName: string;
    totalAssets: number;
    errorsDetected: number;
    warningsDetected?: number;
    businessEntitiesReady: number;
    businessEntitiesCreated: number;
    validationErrors: ValidationError[];
    validationWarnings?: ValidationError[];
    onContinue: () => void;
    onPrev: () => void;
    validationResponse: UploadAssetValidationResponse | null;
}

export const FindingsStep: React.FC<FindingsStepProps> = ({
    darkMode,
    fileName,
    totalAssets,
    errorsDetected,
    warningsDetected = 0,
    businessEntitiesReady,
    businessEntitiesCreated,
    validationErrors,
    validationWarnings = [],
    onContinue,
    onPrev,
    validationResponse
}) => {
    const { t } = useTranslation()
    const logAnalyticsEvent = useAnalyticsEvent();
     useEffect(() => {
                logAnalyticsEvent('user_viewed_findings_step', { description: 'User viewed the findings step in upload assets' });
            },[]);
    return (
        <>
            <div className="findings-header">
                <Typography
                    variant="P1"
                    color={darkMode ? "white" : "black"}
                    className="findings-description"
                >
                    {t('uploadAssets.findingsStep.description')}
                </Typography>
            </div>

            <div className="file-info-section">
                <div className="file-info-row">
                    <Typography variant="P2" color={darkMode ? "white" : "black"}>
                        <strong>{t('uploadAssets.findingsStep.fileName')}:</strong>
                    </Typography>
                    <Typography variant="P2" color={darkMode ? "white" : "black"}>
                        {fileName}
                    </Typography>
                </div>
                <div className="file-info-row">
                    <Typography variant="P2" color={darkMode ? "white" : "black"}>
                        <strong>{t('uploadAssets.findingsStep.assets')}:</strong>
                    </Typography>
                    <Typography variant="P2" color={darkMode ? "white" : "black"}>
                        {validationResponse?.totalRows ?? 0}
                    </Typography>
                </div>
            </div>

                {/* Asset criteria section removed as per code review checklist */}

            <div className="validation-results-section">
                {!validationResponse?.isValid && validationResponse?.message && (
                    <CustomAlert
                        type="error"
                        message={validationResponse.message}
                        darkMode={darkMode}
                        className="error-alert"
                    />
                )}
                {validationResponse?.rowsWithIssue && validationResponse?.rowsWithIssue?.length > 0 && (
                    <CustomAlert
                        type="error"
                        message={t('uploadAssets.findingsStep.errorsDetected', { count: validationResponse.rowsWithIssue.map((r) => r.issues).flat().length, entities: validationResponse.invalidRows, asset: t('uploadAssets.findingsStep.asset', 'Asset') })}
                        darkMode={darkMode}
                        className="error-alert"
                    />
                )}
                {warningsDetected > 0 && (
                    <CustomAlert
                        type="warning"
                        message={t('uploadAssets.findingsStep.warningsDetected', { count: warningsDetected, asset: t('uploadAssets.findingsStep.asset', 'Asset') })}
                        darkMode={darkMode}
                        className="warning-alert"
                    />
                )}
                {validationResponse?.isValid && (
                    <CustomAlert
                        type="success"
                        message={t('uploadAssets.findingsStep.allGood', 'All assets are valid and ready to upload!')}
                        darkMode={darkMode}
                        className="success-alert"
                    />
                )}
            </div>

                {/* What next section removed as per code review checklist */}

            {validationResponse && validationResponse.rowsWithIssue.length > 0 && (
                <div className="error-table-section">
                    <div className="error-table">
                        <div className="table-header">
                            <div className="header-cell">Row</div>
                            <div className="header-cell">{t('uploadAssets.findingsStep.table.errorMessage')}</div>
                        </div>
                        {validationResponse?.rowsWithIssue?.map((row) => (
                            <div key={row.rowNumber ? `row-${row.rowNumber}` : `row-${row.issues?.join('-')}`} className="table-row">
                                <div className="table-cell">{row.rowNumber}</div>
                                <div className="table-cell error-message">
                                    <ul className="issues-list">
                                        {row.issues?.map((issue) => (
                                            <li key={issue}>{issue}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {Array.isArray(validationWarnings) && validationWarnings.length > 0 && (
                <div className="warning-table-section">
                    <div className="warning-table">
                        <div className="table-header">
                            <div className="header-cell">#</div>
                            <div className="header-cell">Row</div>
                            <div className="header-cell">{t('uploadAssets.findingsStep.table.field')}</div>
                            <div className="header-cell">{t('uploadAssets.findingsStep.table.sampleValue')}</div>
                            <div className="header-cell">{t('uploadAssets.findingsStep.table.warningMessage', 'Warning')}</div>
                            <div className="header-cell">{t('uploadAssets.findingsStep.table.suggestedFix')}</div>
                        </div>
                        {validationWarnings.map((warn) => (
                            <div key={warn.row ? `${warn.row}-${warn.field}` : warn.field} className="table-row">
                                <div className="table-cell">{warn.row ?? '-'}</div>
                                <div className="table-cell">{warn.field}</div>
                                <div className="table-cell">{warn.sampleValue}</div>
                                <div className="table-cell warning-message">{warn.errorMessage}</div>
                                <div className="table-cell">{warn.suggestedFix}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="action-buttons-section">
                <Button
                    darkMode={darkMode}
                    label={t('uploadAssets.buttons.prev', 'Prev')}
                    size="md"
                    variant="secondary"
                    onClick={onPrev}
                />
                <Button
                    darkMode={darkMode}
                    label={t('uploadAssets.buttons.continue', 'Continue')}
                    size="md"
                    variant="primary"
                    onClick={onContinue}
                    disabled={!validationResponse?.isValid}
                />
            </div>
        </>
    )
}
